import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "./AuthContext";
import type { PeriodConfig } from "../types/academicPeriod";
import { findUserFamilyFallback } from "../utils/authCheck";

interface Family {
  id: string;
  name: string;
  groupType?: "familia" | "equipo" | "pareja" | "amigos" | "grado" | "curso" | "grupo";
  licenseType?: "familia" | "educacion";
  ownerId?: string;
  admins?: string[];
  members?: string[];
  showAnalytics?: boolean;
  periodConfig?: PeriodConfig;
}

interface ActiveFamilyContextType {
  activeFamily: Family | null;
  setActiveFamily: (family: Family | null) => void;
  availableFamilies: Family[];
  loading: boolean;
  familyChangeKey: number;
}

const ActiveFamilyContext = createContext<ActiveFamilyContextType | undefined>(
  undefined
);

export function ActiveFamilyProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [activeFamily, setActiveFamilyState] = useState<Family | null>(null);
  const [availableFamilies, setAvailableFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [familyChangeKey, setFamilyChangeKey] = useState(0);
  const initialLoadRef = useRef(false);
  const familyListenersCleanupRef = useRef<(() => void)[]>([]);
  const isMountedRef = useRef(true);

  // Función para cambiar la familia
  const setActiveFamily = useCallback((family: Family | null) => {
    if (family) {
      localStorage.setItem("activeFamilyId", family.id);
      setFamilyChangeKey((prev) => prev + 1);
    } else {
      localStorage.removeItem("activeFamilyId");
    }
    setActiveFamilyState(family);
  }, []);

  // Cargar familias del usuario y reaccionar a cambios en tiempo real (familyId/familyIds)
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user?.uid) {
      initialLoadRef.current = false;
      setAvailableFamilies([]);
      setActiveFamily(null);
      setLoading(false);
      return;
    }

    
    setLoading(true);
    const userDocRef = doc(db, "users", user.uid);
    const unsub = onSnapshot(userDocRef, async (userDocSnap) => {
      try {
        // Limpiar listeners previos de familias para evitar acumulación si el doc del usuario cambia.
        // (El return dentro de este callback NO es usado por Firebase para cleanup)
        if (familyListenersCleanupRef.current.length > 0) {
          familyListenersCleanupRef.current.forEach((fn) => {
            try {
              fn();
            } catch {
              // ignore
            }
          });
          familyListenersCleanupRef.current = [];
        }

        if (!userDocSnap.exists()) {
          setAvailableFamilies([]);
          setActiveFamily(null);
          setLoading(false);
          return;
        }

        const userData = userDocSnap.data();
        
        // Extraer familyIds soportando múltiples formatos
        let rawIds: string[] = [];
        if (Array.isArray(userData.familyIds)) {
          rawIds.push(...userData.familyIds);
        } else if (userData.familyIds && typeof userData.familyIds === 'object') {
          rawIds.push(...Object.keys(userData.familyIds));
        }
        if (userData.familyId) {
          rawIds.push(userData.familyId);
        }
        if (userData.activeFamilyId) {
          rawIds.push(userData.activeFamilyId);
        }

        let familyIds = Array.from(new Set(rawIds)).filter(
          (id): id is string => typeof id === 'string' && id.trim().length > 0
        );

        // Fallback: si no hay familyIds en userDoc, buscar en la colección members
        if (familyIds.length === 0) {
          try {
            const fallbackFamId = await findUserFamilyFallback(user.uid, user.email);
            if (fallbackFamId) {
              familyIds = [fallbackFamId];
            }
          } catch (e) {
            console.warn("Fallback family search error:", e);
          }
        }

        if (familyIds.length === 0) {
          setAvailableFamilies([]);
          setActiveFamily(null);
          setLoading(false);
          return;
        }

        // Crear listeners para cada familia individualmente (en tiempo real)
        const familyUnsubs: (() => void)[] = [];
        const familiesMap = new Map<string, Family>();
        let familiesLoaded = 0;

        const handleFamilyResolution = () => {
          familiesLoaded++;
          const updatedFamilies = Array.from(familiesMap.values()).filter(f => !(f as any).isDeleted);
          setAvailableFamilies(updatedFamilies);

          if (!initialLoadRef.current) {
            const savedFamilyId = localStorage.getItem("activeFamilyId");
            const savedFamily = updatedFamilies.find((f) => f.id === savedFamilyId);

            if (savedFamily) {
              initialLoadRef.current = true;
              setActiveFamilyState(savedFamily);
              setLoading(false);
            } else if (!savedFamilyId || familiesLoaded >= familyIds.length) {
              if (updatedFamilies.length > 0) {
                initialLoadRef.current = true;
                const nextFam = updatedFamilies[0];
                localStorage.setItem("activeFamilyId", nextFam.id);
                setActiveFamilyState(nextFam);
                setLoading(false);
              } else if (familiesLoaded >= familyIds.length) {
                initialLoadRef.current = true;
                setActiveFamilyState(null);
                setLoading(false);
              }
            }
          } else {
            setActiveFamilyState((current) => {
              if (current && !familiesMap.has(current.id) && updatedFamilies.length > 0) {
                localStorage.setItem("activeFamilyId", updatedFamilies[0].id);
                return updatedFamilies[0];
              } else if (current && !familiesMap.has(current.id)) {
                localStorage.removeItem("activeFamilyId");
                return null;
              }
              return current;
            });
          }
          setFamilyChangeKey((prev) => prev + 1);
        };

        familyIds.forEach((familyId) => {
          const familyDocRef = doc(db, "families", familyId);
          const unsubFamily = onSnapshot(
            familyDocRef,
            (familyDocSnap) => {
              if (familyDocSnap.exists()) {
                const familyData = familyDocSnap.data() as any;

                // Si la familia fue marcada como eliminada, removerla del listado
                if (familyData?.isDeleted === true) {
                  familiesMap.delete(familyId);
                  handleFamilyResolution();
                  return;
                }
                
                // Mapear customPeriods a periodConfig si existe
                const periodConfig: PeriodConfig | undefined = familyData.customPeriods && familyData.customPeriods.length > 0
                  ? {
                      type: familyData.gradePeriodType || 'trimester',
                      academicYearStart: familyData.customPeriods[0]?.startDate || '',
                      academicYearEnd: familyData.customPeriods[familyData.customPeriods.length - 1]?.endDate || '',
                      currentPeriod: 1, // Se calculará dinámicamente
                      currentAcademicYear: parseInt(familyData.academicYear) || new Date().getFullYear(),
                      periods: familyData.customPeriods.map((p: any) => ({
                        id: p.periodNumber,
                        name: p.name,
                        startDate: p.startDate,
                        endDate: p.endDate
                      }))
                    }
                  : undefined;
                
                const family = { 
                  id: familyDocSnap.id, 
                  ...familyData,
                  periodConfig 
                } as Family;
                
                familiesMap.set(familyId, family);
                handleFamilyResolution();

                // Actualizar familia activa si es la que está seleccionada
                setActiveFamilyState((current) => {
                  if (current?.id === familyId) {
                    return family;
                  }
                  return current;
                });
              } else {
                // Familia no existe o fue eliminada de Firestore
                familiesMap.delete(familyId);
                const savedFamilyId = localStorage.getItem("activeFamilyId");
                if (savedFamilyId === familyId) {
                  localStorage.removeItem("activeFamilyId");
                }
                handleFamilyResolution();
              }
            },
            (error) => {
              console.error(`Error al escuchar cambios de la familia ${familyId}:`, error);
              familiesMap.delete(familyId);
              handleFamilyResolution();
            }
          );
          familyUnsubs.push(unsubFamily);
        });

        // Guardar cleanup actual para el próximo snapshot
        familyListenersCleanupRef.current = familyUnsubs;

        // Si no hay familias, terminar loading inmediatamente
        if (familyIds.length === 0) {
          setLoading(false);
        }

      } catch (error) {
        setAvailableFamilies([]);
        setLoading(false);
      }
    });

    return () => {
      isMountedRef.current = false;
      try {
        unsub();
      } catch (e) {
        // Ignore errors when listener is already closed
      }
    };
  }, [user?.uid]);

  return (
    <ActiveFamilyContext.Provider
      value={{
        activeFamily,
        setActiveFamily,
        availableFamilies,
        loading,
        familyChangeKey,
      }}
    >
      {children}
    </ActiveFamilyContext.Provider>
  );
}

export function useActiveFamily(): ActiveFamilyContextType {
  const context = useContext(ActiveFamilyContext);
  if (context === undefined) {
    throw new Error(
      "useActiveFamily must be used within an ActiveFamilyProvider"
    );
  }
  return context;
}
